import { TreeAdapter } from '@abp/ng.components/tree';
import { ABP, ListService, PagedResultDto } from '@abp/ng.core';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { NzTreeNode } from 'ng-zorro-antd/tree';
import { LocationTreeNode } from '@/locations/models/location-tree-node.model';
import { GetLocationsInput, LocationDefinition } from '@register/proxy/register-service/locations';
import { finalize, Observable, Subscription } from 'rxjs';
import { LocationService } from '@proxy/register-service/locations';
import { AssetService } from '@proxy/register-service/assets';

@Component({
  selector: 'app-locations-tree',
  templateUrl: './locations-tree.component.html',
  styleUrls: ['./locations-tree.component.scss'],
})
export class LocationsTreeComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isLoading!: boolean;
  @Input() siteId!: string;
  @Input() checkStrictly = false;
  @Input() checkable = false;
  @Input() getAll = false;
  @Input() selectedNode!: LocationTreeNode;
  @Input() selectedNodes: LocationTreeNode[] = [];
  @Input() restrictedKeys: string[] = [];
  @Input() readonly!: boolean;
  @Input() filters!: GetLocationsInput;
  @Input() treeTitle!: string;
  @Input() locationDefinition!: LocationDefinition;
  @Input() hasAssociatedInspections!: boolean;
  @Input() hasAssociatedAssets!: boolean;
  @Input() parentId = '00000000-0000-0000-0000-000000000000';
  @Input() displayHeaders!: boolean;
  @Input() hideCreate = false;
  @Input() isAllSelected = false;
  @Input() showChildLocations = true;
  @Input() pinOrKeyringPassword!: string;
  @Input() withLookup = false;
  @Input() isGuest: boolean = false;
  @Input() data: PagedResultDto<LocationTreeNode> = {
    items: [],
    totalCount: 0,
  };
  @Input() subAssetsNotes: string = '';
  @Input() transferNotes: string = '';
  @Input() showTransferNotes: boolean = false;
  @Input() showSubAssetsNotes = false;

  @Output() addRootNode = new EventEmitter<LocationDefinition>();
  @Output() addSubNode = new EventEmitter<LocationTreeNode>();
  @Output() deleteNode = new EventEmitter<LocationTreeNode>();
  @Output() toggleActiveNode = new EventEmitter<LocationTreeNode>();
  @Output() selectedNodeChange = new EventEmitter<LocationTreeNode>();
  @Output() checkedKeysChange = new EventEmitter<string[]>();
  @Output() restrictedKeysChange = new EventEmitter<{
    type: LocationDefinition;
    items: string[];
  }>();
  @Output() subAssetsNotesChange = new EventEmitter<string>();
  @Output() transferNotesChange = new EventEmitter<string>();

  selectedLocation: LocationTreeNode | null = null;
  expandedKeys: any[] = [];
  allKeys: string[] = [];
  nodes: LocationTreeNode[] = [];
  treeAdapter!: TreeAdapter<LocationTreeNode>;
  locationDefinitionEnum = LocationDefinition;

  private subscriptions: Subscription[] = [];
  private _checkedKeys: string[] = [];

  @Input()
  get checkedKeys(): string[] {
    return this._checkedKeys;
  }

  get withoutAssetId(): boolean {
    const fullUrl = this.router.url;
    return fullUrl.includes('jobsite/edit') || fullUrl.includes('assets/view');
  }

  set checkedKeys(value: string[]) {
    if (this.isAllSelected) {
      const set1 = new Set(this.allKeys.concat(this.restrictedKeys));
      const set2 = new Set(value);
      const uniqueArr = Array.from(new Set([...set1].filter(item => !set2.has(item)))).filter(e =>
        this.allKeys.includes(e),
      );
      this.restrictedKeysChange.emit({ type: this.locationDefinition, items: uniqueArr });
    }

    this._checkedKeys = value;
    this.checkedKeysChange.emit(this._checkedKeys);
  }

  constructor(
    public readonly list: ListService,
    public readonly service: LocationService,
    public readonly assetService: AssetService,
    public readonly cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit() {
    const routeSub = this.route.paramMap.subscribe((params: ParamMap) => {
      const getData = (query: ABP.PageQueryParams) => {
        this.isLoading = true;
        let request: Observable<any>;

        if (this.withLookup) {
          request = this.service.getLookup(
            {
              ...query,
              ...this.filters,
              assetId: this.withoutAssetId ? undefined : (params.get('id') ?? undefined),
              siteId: this.siteId,
              getAll: this.getAll,
              pinOrKeyringPassword: this.pinOrKeyringPassword,
              locationDefinition: this.locationDefinition,
              hasAssociatedInspections: this.hasAssociatedInspections,
              hasAssociatedAssets: this.hasAssociatedAssets,
              parentId: this.showChildLocations ? undefined : this.parentId,
              term: this.isAllSelected ? '' : query.filter,
              skipCount: 0,
              maxResultCount: 1000,
            },
            this.isGuest,
          );
        } else {
          request = this.service.getList({
            ...query,
            ...this.filters,
            assetId: this.withoutAssetId ? undefined : (params.get('id') ?? undefined),
            siteId: this.siteId,
            getAll: this.getAll,
            pinOrKeyringPassword: this.pinOrKeyringPassword,
            locationDefinition: this.locationDefinition,
            parentId: this.showChildLocations ? undefined : this.parentId,
            term: this.isAllSelected ? '' : query.filter,
            skipCount: 0,
            maxResultCount: 1000,
          });
        }

        return request.pipe(finalize(() => (this.isLoading = false)));
      };

      const setData = (list: PagedResultDto<LocationTreeNode>) => {
        this.setTreeAdapterData(list);
        this.selectedLocation = null;

        if (this.isAllSelected) {
          this.allKeys = (this.data.items ?? []).map(e => e.id);
          this.checkedKeys = this.allKeys.filter(e => !this.restrictedKeys.includes(e));
        }

        this.cdr.markForCheck();
      };

      const dataSub = (this.list.hookToQuery(getData) as any).subscribe(setData);
      this.subscriptions.push(dataSub);
    });

    this.subscriptions.push(routeSub);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.siteId || changes.parentId) {
      this.list.get();
    }

    if (changes.data && !changes.data.firstChange) {
      this.setTreeAdapterData(changes.data.currentValue);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  setTreeAdapterData(list: PagedResultDto<LocationTreeNode>) {
    if (!list) return;

    this.data = list;

    const items = list.items ?? [];
    const allIds = new Set(items.map(i => i.id));

    const processedItems = items.map(item => {
      if (item.parentId && !allIds.has(item.parentId)) {
        return { ...item, parentId: null };
      }
      return item;
    });

    this.treeAdapter = new TreeAdapter(processedItems);
    this.nodes = this.treeAdapter.getTree();
    this.expandedKeys = [...this.expandedKeys];
    this.cdr.markForCheck();
  }

  onAddRootNode() {
    this.addRootNode.emit(this.locationDefinition);
  }

  onAddSubNode(node: NzTreeNode, $event: MouseEvent) {
    this.addSubNode.emit(node.origin.entity);
    $event.stopPropagation();
  }

  onDelete(node: NzTreeNode, $event: MouseEvent) {
    this.deleteNode.emit(node.origin.entity);
    $event.stopPropagation();
  }

  onToggleActive(node: NzTreeNode, $event: MouseEvent) {
    this.toggleActiveNode.emit(node.origin.entity);
    $event.stopPropagation();
  }

  isNodeSelected(node: NzTreeNode) {
    if (this.locationDefinition === this.locationDefinitionEnum.JOBSITE && this.readonly) {
      return this.selectedNodes.includes(node.origin.entity);
    } else {
      return node.origin.id === this.selectedNode?.id;
    }
  }

  onSelectedNode(node: LocationTreeNode) {
    this.selectedNodeChange.emit(node);
  }

  onCheckedKeysChange(newKeys: string[]) {
    if (this.isAllSelected) {
      const prevKeys = new Set(this._checkedKeys);
      const nextKeys = new Set(newKeys);

      const added = newKeys.filter(k => !prevKeys.has(k));
      const removed = [...prevKeys].filter(k => !nextKeys.has(k));

      const finalKeys = new Set(newKeys);

      for (const key of added) {
        this.getDescendantKeys(key).forEach(d => finalKeys.add(d));
      }
      for (const key of removed) {
        this.getDescendantKeys(key).forEach(d => finalKeys.delete(d));
      }

      this.checkedKeys = Array.from(finalKeys);
    } else {
      this.checkedKeys = newKeys;
    }
  }

  getDescendantKeys(key: string): string[] {
    const result: string[] = [];
    const queue = [key];
    while (queue.length) {
      const current = queue.shift();
      const children = (this.data?.items || []).filter(i => i.parentId === current);
      for (const child of children) {
        result.push(child.id);
        queue.push(child.id);
      }
    }
    return result;
  }
}
